/**
 * Main Application Component
 * Sets up routing and providers with locale-aware URL support.
 *
 * Routing strategy:
 * - Default locale (en-US): bare paths (e.g., /products)
 * - Non-default locales: prefixed paths (e.g., /en-CA/products, /en-GB/products)
 */

import React from 'react';
import { RouterProvider, createBrowserRouter, Outlet, useLocation, useMatches } from 'react-router-dom';
import styled from '@emotion/styled';
import { Providers } from './app/providers';
import { Header } from './components/Header/Header';
import { Footer } from './components/Footer/Footer';
import { SideCartWrapper } from './components/organisms/SideCart/SideCartWrapper';
import { IntlProvider, LocalePricingInvalidator, DEFAULT_LOCALE, SUPPORTED_LOCALES } from './lib/intl';
import { CountrySelectorPopup } from './components/CountrySelectorPopup/CountrySelectorPopup';
import type { SupportedLocale } from './lib/intl';
import { LayoutModeProvider, useLayoutMode } from './lib/context/layout-context';

/**
 * Route-level fade-in wrapper.
 * Keyed on location.pathname so each navigation plays the animation once.
 */
const RouteTransition = styled.div`
  animation: routeFadeIn 0.2s ease;

  @keyframes routeFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * Scroll to top on route change.
 * Without this, navigating from a scrolled position (e.g. product grid)
 * to a PDP or collection detail keeps the scroll position mid-page.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AnimatedOutlet() {
  const location = useLocation();
  return (
    <>
      <ScrollToTop />
      <RouteTransition key={location.pathname}>
        <Outlet />
      </RouteTransition>
    </>
  );
}

// Page imports
import { HomePage } from './pages/HomePage';
import { ProductsPage } from './pages/ProductsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { PackPage } from './pages/PackPage';
import { CollectionsPage } from './pages/CollectionsPage';
import { CollectionDetailPage } from './pages/CollectionDetailPage';
import { SearchResultsPage } from './pages/SearchResultsPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { MyAccountPage } from './pages/MyAccountPage';
import { AboutPage } from './pages/AboutPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { LandingPage } from './pages/LandingPage';
import { CartPage } from './pages/CartPage';
import { ThankYouPage } from './pages/ThankYouPage';
import { PostPurchasePage } from './pages/PostPurchasePage';

// Layoutless page imports — these routes bypass the full locale/chrome shell
import { ClearCartPage } from './pages/ClearCartPage';
import { CheckoutReturnPage } from './pages/CheckoutReturnPage';
import { EmbedProductPage } from './pages/EmbedProductPage';
import { FeedsPage } from './pages/FeedsPage';

/**
 * Layoutless route wrapper.
 * Provides IntlProvider with the default locale (for any components that use i18n)
 * but renders NO chrome: no Header, no Footer, no SideCart, no CountrySelectorPopup.
 * Global providers (QueryClient, Theme, Cart, Device, Funnel) are still available
 * because they wrap the entire RouterProvider in <Providers>.
 */
function LayoutlessWrapper() {
  return (
    <IntlProvider locale={DEFAULT_LOCALE}>
      <main id="main-content">
        <Outlet />
      </main>
    </IntlProvider>
  );
}

/**
 * Locale-aware layout component.
 * Receives an explicit locale prop — no wildcard URL matching.
 *
 * Reads route metadata (`handle.bare`) via useMatches and passes it to
 * LayoutModeProvider. Pages can also call `useLayoutMode().setBare(true)`
 * at runtime. When bare mode is active, Header and Footer are hidden.
 */
function LocaleLayout({ locale }: Readonly<{ locale: SupportedLocale }>) {
  const matches = useMatches();
  const routeBare = matches.some(
    (m) => (m.handle as Record<string, unknown> | undefined)?.bare === true,
  );

  return (
    <IntlProvider locale={locale}>
      <LocalePricingInvalidator />
      <LayoutModeProvider routeBare={routeBare}>
        <LayoutShell />
      </LayoutModeProvider>
      <CountrySelectorPopup />
    </IntlProvider>
  );
}

/** Inner shell that reads LayoutModeContext to conditionally show chrome. */
function LayoutShell() {
  const { bare } = useLayoutMode();

  if (bare) {
    return (
      <>
        <AnimatedOutlet />
        <SideCartWrapper />
      </>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <main style={{ flex: 1 }}>
        <AnimatedOutlet />
      </main>
      <Footer />
      <SideCartWrapper />
    </div>
  );
}


// Shared child routes used by both the prefixed and bare route groups
const pageRoutes = [
  {
    index: true,
    element: <HomePage />,
  },
  {
    path: 'products',
    element: <ProductsPage />,
  },
  {
    path: 'products/:slug',
    element: <ProductDetailPage />,
  },
  {
    path: 'packs/:slug',
    element: <PackPage />,
  },
  {
    path: 'collections',
    element: <CollectionsPage />,
  },
  {
    path: 'collections/:slug',
    element: <CollectionDetailPage />,
  },
  {
    path: 'search',
    element: <SearchResultsPage />,
  },
  {
    path: 'login',
    element: <LoginPage />,
  },
  {
    path: 'register',
    element: <RegisterPage />,
  },
  {
    path: 'my-account',
    element: <MyAccountPage />,
  },
  {
    path: 'about',
    element: <AboutPage />,
  },
  {
    path: 'checkout/:token',
    element: <CheckoutPage />,
  },
  {
    path: 'lp/:slug',
    element: <LandingPage />,
    handle: { bare: true },
  },
  {
    path: 'cart',
    element: <CartPage />,
  },
  {
    path: 'thank-you',
    element: <ThankYouPage />,
  },
  {
    path: 'post-purchase',
    element: <PostPurchasePage />,
  },
];

// Generate an explicit route entry for each non-default locale.
// This avoids a wildcard /:locale param that would match arbitrary
// first segments (e.g. /foo) and silently fall back to the default locale.
const localeRoutes = SUPPORTED_LOCALES
  .filter((l) => l !== DEFAULT_LOCALE)
  .map((locale) => ({
    path: `/${locale}`,
    element: <LocaleLayout locale={locale} />,
    children: pageRoutes,
  }));

// Layoutless routes — no header, footer, or side cart.
// These sit before locale routes so they match first.
const layoutlessRoutes = [
  { path: '/embed-product/:slug', element: <EmbedProductPage /> },
  { path: '/feeds', element: <FeedsPage /> },
  { path: '/clear-cart', element: <ClearCartPage /> },
  { path: '/checkout-return', element: <CheckoutReturnPage /> },
];

const routerBasename =
  typeof window !== 'undefined' && window.location.pathname.startsWith('/clone')
    ? '/clone'
    : '/';

const router = createBrowserRouter(
  [
    // Layoutless routes — bypass LocaleLayout/LayoutShell entirely
    {
      element: <LayoutlessWrapper />,
      children: layoutlessRoutes,
    },
    // Explicit routes for each non-default locale: /en-CA/*, /en-GB/*, etc.
    ...localeRoutes,
    // Default locale (en-US): /products/:slug, etc.
    {
      path: '/',
      element: <LocaleLayout locale={DEFAULT_LOCALE} />,
      children: pageRoutes,
    },
  ],
  { basename: routerBasename },
);

export function App() {
  return (
    <Providers>
      <RouterProvider router={router} />
    </Providers>
  );
}
